import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { portfolioAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';

const PortfolioTab = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ title: '', category: '', description: '', image: '', year: '' });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await portfolioAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectForm.image) {
      toast.error('Carica un\'immagine per il progetto');
      return;
    }
    try {
      if (editingProject) {
        await portfolioAPI.update(editingProject.id, projectForm);
        toast.success('Progetto aggiornato!');
      } else {
        await portfolioAPI.create(projectForm);
        toast.success('Progetto aggiunto!');
      }
      loadProjects();
      setIsDialogOpen(false);
      setEditingProject(null);
      setProjectForm({ title: '', category: '', description: '', image: '', year: '' });
    } catch (error) {
      toast.error('Errore nel salvare il progetto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo progetto?')) return;
    try {
      await portfolioAPI.delete(id);
      toast.success('Progetto eliminato!');
      loadProjects();
    } catch (error) {
      toast.error('Errore nell\'eliminare il progetto');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setProjectForm({
      title: project.title,
      category: project.category,
      description: project.description,
      image: project.image,
      year: project.year
    });
    setIsDialogOpen(true);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione Portfolio</CardTitle>
            <CardDescription>Aggiungi, modifica o elimina progetti</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-portfolio-btn"
                onClick={() => { 
                  setEditingProject(null); 
                  setProjectForm({ title: '', category: '', description: '', image: '', year: '' }); 
                }}
              >
                <Plus className="w-4 h-4 mr-2" />Aggiungi Progetto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle>
                <DialogDescription>{editingProject ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Titolo Progetto</Label>
                  <Input 
                    id="title" 
                    data-testid="portfolio-title-input"
                    value={projectForm.title} 
                    onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <Input 
                    id="category" 
                    data-testid="portfolio-category-input"
                    value={projectForm.category} 
                    onChange={(e) => setProjectForm({ ...projectForm, category: e.target.value })} 
                    required 
                    className="mt-2" 
                    placeholder="Hospital, Laboratory, etc." 
                  />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={projectForm.description}
                      onChange={(value) => setProjectForm({ ...projectForm, description: value })}
                      placeholder="Descrizione del progetto..."
                      minHeight="150px"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="year">Anno</Label>
                  <Input 
                    id="year" 
                    data-testid="portfolio-year-input"
                    value={projectForm.year} 
                    onChange={(e) => setProjectForm({ ...projectForm, year: e.target.value })} 
                    required 
                    className="mt-2" 
                    placeholder="2023" 
                  />
                </div>
                <div>
                  <Label>Immagine</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={projectForm.image}
                      onChange={(url) => setProjectForm({ ...projectForm, image: url })}
                      uploadType="image"
                      label="Carica immagine progetto"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="submit-portfolio-btn">{editingProject ? 'Aggiorna' : 'Crea'} Progetto</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Immagine</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Anno</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : projects.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessun progetto</TableCell></TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow key={project.id} data-testid={`portfolio-row-${project.id}`}>
                    <TableCell><img src={project.image} alt={project.title} className="w-12 h-12 rounded-md object-cover" /></TableCell>
                    <TableCell className="font-medium">{project.title}</TableCell>
                    <TableCell><Badge variant="secondary">{project.category}</Badge></TableCell>
                    <TableCell>{project.year}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(project)} data-testid={`edit-portfolio-${project.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(project.id)} className="text-destructive hover:text-destructive" data-testid={`delete-portfolio-${project.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioTab;
